import { Navigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, MonitorPlay } from "lucide-react";

const API = "/api";

export const getAuthToken = () => localStorage.getItem("auth_token");
export const getAuthUsername = () => localStorage.getItem("auth_username");

export const clearAuth = () => {
  localStorage.removeItem("auth_token");
  localStorage.removeItem("auth_username");
  localStorage.removeItem("auth_expires");
};

const isTokenExpired = () => {
  const expires = localStorage.getItem("auth_expires");
  if (!expires) return true;
  return Date.now() / 1000 > parseInt(expires, 10);
};

export const setupAxiosAuth = () => {
  axios.interceptors.request.use(
    (config) => {
      const token = getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const isAuthEndpoint = error.config?.url?.includes("/api/auth/login");
      if (error.response?.status === 401 && !isAuthEndpoint) {
        clearAuth();
        window.location.href = "/login";
      }
      return Promise.reject(error);
    }
  );
};

export default function ProtectedRoute({ children }) {
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const token = getAuthToken();

      if (!token) {
        setIsAuthenticated(false);
        setIsChecking(false);
        return;
      }

      if (isTokenExpired()) {
        clearAuth();
        setIsAuthenticated(false);
        setIsChecking(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.authenticated) {
          setIsAuthenticated(true);
        } else {
          clearAuth();
          setIsAuthenticated(false);
        }
      } catch (err) {
        clearAuth();
        setIsAuthenticated(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkAuth();
  }, []);

  if (isChecking) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <MonitorPlay className="w-12 h-12 text-white/40 mx-auto mb-4 animate-pulse" />
          <Loader2 className="w-6 h-6 text-white/60 animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}
